// src/pages/SupportSla.jsx
// Configuración de umbrales SLA para Atención a Clientes (admin/leader).
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';

const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    check: <Icon><path d="M20 6 9 17l-5-5" /></Icon>,
    refresh: <Icon><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Icon>,
    info: <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></Icon>,
    clock: <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.sp-root {
    --sp-accent: var(--brand-700, #0a4aa8);
    --sp-accent-hover: var(--brand-800, #07357f);
    --sp-accent-light: #dbeafe;
    --sp-accent-ring: rgba(10, 74, 168, .12);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 820px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.sp-root * { box-sizing: border-box; }
.sp-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.sp-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0 0 20px; line-height: 1.5; }
.sp-card {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    padding: 22px 26px;
    margin-bottom: 16px;
}
.sp-group { margin-bottom: 18px; }
.sp-group:last-child { margin-bottom: 0; }
.sp-label {
    display: block; font-size: 13px; font-weight: 600;
    color: var(--c-text-2); margin-bottom: 6px;
}
.sp-hint {
    font-size: 12px; color: var(--c-text-4); margin: 6px 0 0; line-height: 1.5;
    display: flex; gap: 6px; align-items: flex-start;
}
.sp-input {
    padding: 9px 12px; font-size: 14px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s;
    width: 160px;
}
.sp-input:focus {
    border-color: var(--sp-accent); box-shadow: 0 0 0 3px var(--sp-accent-ring);
    background: var(--c-card);
}
.sp-info {
    background: var(--c-status-open-bg);
    border: 1px solid #93c5fd;
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 13px;
    color: #1e3a8a;
    margin-bottom: 18px;
}
[data-bs-theme="dark"] .sp-info { background: rgba(37,99,235,.08); border-color: rgba(37,99,235,.3); color: #93c5fd; }
.sp-info strong { display: block; margin-bottom: 8px; font-size: 13px; }
.sp-info-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.sp-info-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.sp-actions { display: flex; gap: 8px; margin-top: 4px; }
.sp-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 9px 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.sp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sp-btn-primary { background: var(--sp-accent); color: #fff; border-color: var(--sp-accent); box-shadow: 0 1px 3px rgba(10,74,168,.25); }
.sp-btn-primary:hover:not(:disabled) { background: var(--sp-accent-hover); border-color: var(--sp-accent-hover); transform: translateY(-1px); }
.sp-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.sp-btn-ghost:hover:not(:disabled) { background: var(--c-msg-bg); }
.sp-meta { margin-top: 14px; font-size: 11px; color: var(--c-text-4); display: flex; align-items: center; gap: 6px; }
`;

export default function SupportSla() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [config, setConfig]   = useState({
        firstResponseMinutes: 15,
        resolutionMinutes:    120,
        warningMinutes:       5,
    });
    const [meta, setMeta] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/support/sla');
            setConfig({
                firstResponseMinutes: data.firstResponseMinutes ?? 15,
                resolutionMinutes:    data.resolutionMinutes    ?? 120,
                warningMinutes:       data.warningMinutes       ?? 5,
            });
            setMeta(data);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cargar');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (config.warningMinutes >= config.firstResponseMinutes) {
            toast.warn('El umbral de advertencia debe ser menor que el de primera respuesta');
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.put('/support/sla', config);
            setMeta(data);
            toast.success('Configuración guardada (se aplica en los próximos ~60s)');
        } catch (e) {
            toast.error(e.response?.data?.error || 'Error al guardar');
        } finally { setSaving(false); }
    };

    if (loading) {
        return (
            <>
                <style>{CSS}</style>
                <div className="sp-root">
                    <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" variant="primary" /></div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{CSS}</style>
            <div className="sp-root">
                <h1 className="sp-title">SLA — Atención a Clientes</h1>
                <p className="sp-subtitle">
                    Umbrales que definen el color de los indicadores de tiempo de espera en el inbox y
                    el % de cumplimiento en los reportes. Los cambios se aplican en ~60 segundos.
                </p>

                <div className="sp-card">
                    <div className="sp-group">
                        <label className="sp-label">Primera respuesta humana (min)</label>
                        <input
                            type="number" min={1} max={1440}
                            className="sp-input"
                            value={config.firstResponseMinutes}
                            onChange={e => setConfig(c => ({ ...c, firstResponseMinutes: Math.max(1, Number(e.target.value) || 1) }))}
                        />
                        <p className="sp-hint">
                            {I.info}
                            Tiempo máximo (en minutos) que un cliente debería esperar desde que entra la
                            consulta hasta la primera respuesta de un agente humano. Default: 15 min.
                        </p>
                    </div>

                    <div className="sp-group">
                        <label className="sp-label">Resolución total (min)</label>
                        <input
                            type="number" min={1} max={10080}
                            className="sp-input"
                            value={config.resolutionMinutes}
                            onChange={e => setConfig(c => ({ ...c, resolutionMinutes: Math.max(1, Number(e.target.value) || 1) }))}
                        />
                        <p className="sp-hint">
                            {I.info}
                            Tiempo máximo (en minutos) desde que se abre la conversación hasta que se cierra.
                            Default: 120 min (2 horas).
                        </p>
                    </div>

                    <div className="sp-group">
                        <label className="sp-label">Umbral de advertencia (min)</label>
                        <input
                            type="number" min={0} max={config.firstResponseMinutes - 1}
                            className="sp-input"
                            value={config.warningMinutes}
                            onChange={e => setConfig(c => ({ ...c, warningMinutes: Math.max(0, Number(e.target.value) || 0) }))}
                        />
                        <p className="sp-hint">
                            {I.info}
                            A partir de este tiempo de espera, el indicador pasa a amarillo (antes de
                            llegar al rojo del threshold principal). Default: 5 min.
                        </p>
                    </div>
                </div>

                <div className="sp-info">
                    <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {I.clock} Cómo se ven los colores en el inbox
                    </strong>
                    <div className="sp-info-row">
                        <span className="sp-info-dot" style={{ background: '#15803d' }} />
                        <strong style={{ color: '#15803d' }}>Verde</strong>
                        <span>menos de {config.warningMinutes} min de espera</span>
                    </div>
                    <div className="sp-info-row">
                        <span className="sp-info-dot" style={{ background: '#b45309' }} />
                        <strong style={{ color: '#b45309' }}>Amarillo</strong>
                        <span>entre {config.warningMinutes} y {config.firstResponseMinutes} min</span>
                    </div>
                    <div className="sp-info-row">
                        <span className="sp-info-dot" style={{ background: '#b91c1c' }} />
                        <strong style={{ color: '#b91c1c' }}>Rojo</strong>
                        <span>más de {config.firstResponseMinutes} min (SLA incumplido)</span>
                    </div>
                </div>

                <div className="sp-actions">
                    <button type="button" className="sp-btn sp-btn-primary" onClick={save} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <>{I.check} Guardar cambios</>}
                    </button>
                    <button type="button" className="sp-btn sp-btn-ghost" onClick={load} disabled={saving}>
                        {I.refresh} Recargar
                    </button>
                </div>

                {meta?.updatedAt && (
                    <div className="sp-meta">
                        Última actualización: {new Date(meta.updatedAt).toLocaleString()}
                        {meta.updatedBy?.name && ` · por ${meta.updatedBy.name}`}
                    </div>
                )}
            </div>
        </>
    );
}
